import * as React from 'react'
import FormLayout from '../../shared/FormLayout'
import StageHeader from '../../shared/StageHeader'
import { DateField } from '@/ui/form/controls/DateField'
import { Divider } from '../../shared/Divider'
import { useContractForm } from '@/modules/drivers/context/ContractFormContext'
import { TextField } from '@/ui/form/controls/TextField'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import useContractService from '@/modules/drivers/services/ContractService'
import { FaIcon, faPencil } from '@/lib/fa'
import type { ContractDetailsModel } from '@/modules/drivers/types/contracts'

const MAX_DAYS_OF_LEAVE = 100

// Daty przychodzą z API z czasem, z pola bez — porównujemy sam dzień.
const comparable = (value: unknown) =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)
    ? value.slice(0, 10)
    : value

const ContractVacations = () => {
  const { t } = useLocalize()
  const {
    data,
    sourceData,
    setData,
    isEditState,
    isAnnexState,
    companyId,
    driverId,
  } = useContractForm()
  const { calculateOverdueLeave } = useContractService()

  // Jak w WPF: znacznik tylko przy nowym aneksie, dla pól zmienionych względem umowy.
  const withModifiedMark = (label: string, key: keyof ContractDetailsModel) => {
    const isModified =
      isAnnexState &&
      !isEditState &&
      !!sourceData &&
      comparable(data?.[key]) !== comparable(sourceData[key])
    if (!isModified) return label
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5">
        {label}
        <span
          role="img"
          title={t('contract.field-modified-in-annex')}
          aria-label={t('contract.field-modified-in-annex')}
        >
          <FaIcon icon={faPencil} className="tw-text-xs tw-text-content-base" />
        </span>
      </span>
    )
  }

  const handleLeaveConfigDayChange = async (date: string | null) => {
    setData((prev) => ({ ...prev, initialLeaveConfigurationDay: date }))

    // Auto-wyliczenie zaległego urlopu z poprzedniego roku — tylko przy dodawaniu
    // umowy dla pojedynczego kierowcy (rok wyznacza data konfiguracji urlopu).
    if (isEditState || !driverId || !date) return
    const year = Number(date.slice(0, 4))
    if (!year) return

    const overdue = await calculateOverdueLeave({ companyId, driverId, year })
    if (overdue != null) {
      setData((prev) => ({ ...prev, daysOfOverdueLeave: overdue }))
    }
  }

  return (
    <div className="tw-flex tw-min-h-[500px] tw-justify-between">
      <StageHeader title={t('contract.vacations')} />
      <FormLayout>
          <DateField
            label={withModifiedMark(t('contract.vacation-status-date'), 'initialLeaveConfigurationDay')}
            value={data?.initialLeaveConfigurationDay}
            setValue={handleLeaveConfigDayChange}
          />

          <Divider />
          <TextField
            label={withModifiedMark(t('contractDetails.workYears'), 'workYears')}
            placeholder={t('common.type')}
            helper={t('contract.vacation-days-hint')}
            value={data?.workYears?.toString()}
            onChange={(e) =>
              setData({ ...data, workYears: Number(e.target.value) })
            }
          />
          <TextField
            label={withModifiedMark(t('contract.vacation-days-by-seniority'), 'daysOfLeave')}
            placeholder={t('common.type')}
            inputMode="numeric"
            name="daysOfLeave"
            value={data?.daysOfLeave?.toString()}
            onChange={(e) => {
              const days = Number(e.target.value.replace(/\D/g, ''))
              if (days > MAX_DAYS_OF_LEAVE) return
              setData((prev) => ({ ...prev, daysOfLeave: days }))
            }}
          />

          <TextField
            label={withModifiedMark(t('contract.overdue-vacation-days'), 'daysOfOverdueLeave')}
            placeholder={t('common.type')}
            value={data?.daysOfOverdueLeave?.toString()}
            onChange={(e) =>
              setData({ ...data, daysOfOverdueLeave: Number(e.target.value) })
            }
          />
          <TextField
            label={withModifiedMark(t('contract.used-on-demand-days'), 'daysOfLeaveAtTheRequest')}
            placeholder={t('common.type')}
            value={data?.daysOfLeaveAtTheRequest?.toString()}
            onChange={(e) =>
              setData({
                ...data,
                daysOfLeaveAtTheRequest: Number(e.target.value),
              })
            }
          />
          <TextField
            label={withModifiedMark(t('contract.used-vacation-days'), 'daysOfUsedLeave')}
            placeholder={t('common.type')}
            value={data?.daysOfUsedLeave?.toString()}
            onChange={(e) =>
              setData({ ...data, daysOfUsedLeave: Number(e.target.value) })
            }
          />
      </FormLayout>
    </div>
  )
}

export default ContractVacations
